import React from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  FormLabel,
  FormHelperText,
  Box,
} from "@mui/material";
import { colors } from "../../../../theme";
import CommonInput from "./CommonInput";

/**
 * CommonFormField Component
 * Centralized, reusable form field component adhering to AutoBET design tokens.
 *
 * Features:
 * - Unified FormLabel with icon support, label text, and required asterisk
 * - Built-in error and helper text state styling
 * - Seamless integration with CommonInput when used as a standard input field
 * - Supports custom inputs or complex form controls (DateTimePicker, File upload, Switch, Select, etc.) via children
 * - Consistent typography, spacing, and theme palette colors
 */
const CommonFormField = ({
  // Form Control & identification
  id,
  name,
  fullWidth = true,
  width,
  error = false,
  disabled = false,
  readOnly = false,
  size = "small",

  // Label & Header Props
  label,
  icon,
  iconColor,
  iconSize = 16,
  required = false,
  requiredColor,
  extraLabel,
  hideLabel = false,
  labelSx = {},

  // Helper & Error Text Props
  helperText,
  errorText,
  hideHelperText = false,
  helperTextSx = {},

  // Built-in CommonInput Props (used when children is not provided)
  value,
  onChange,
  placeholder,
  type = "text",
  startAdornment,
  endAdornment,
  inputProps = {},
  inputSx = {},

  // Custom Controls via children
  children,

  // Custom styling & root props
  sx = {},
  formControlProps = {},
  ...restProps
}) => {
  const isError = Boolean(error);
  const resolvedHelperText = typeof error === "string" ? error : (error ? (errorText || helperText) : helperText);
  const resolvedWidth = fullWidth ? "100%" : width;
  const resolvedRequiredColor =
    requiredColor || colors.dashboard?.status?.dangerText || colors.error?.main || "#ef4444";

  return (
    <FormControl
      fullWidth={fullWidth}
      error={isError}
      disabled={disabled}
      sx={{
        ...(resolvedWidth ? { width: resolvedWidth } : {}),
        ...sx,
      }}
      {...formControlProps}
    >
      {/* Label Header */}
      {!hideLabel && (label || icon) && (
        <FormLabel
          htmlFor={id}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.5,
            fontWeight: 600,
            fontSize: "14px",
            color: `${colors.slate[700]} !important`,
            "&.Mui-focused": {
              color: `${colors.slate[700]} !important`,
            },
            ...labelSx,
          }}
        >
          {icon && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: iconColor || colors.slate[500],
                flexShrink: 0,
                "& svg": {
                  width: iconSize,
                  height: iconSize,
                },
              }}
            >
              {icon}
            </Box>
          )}

          {label && (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
              {label}
              {required && (
                <Box
                  component="span"
                  sx={{
                    color: resolvedRequiredColor,
                    ml: 0.5,
                  }}
                >
                  *
                </Box>
              )}
            </Box>
          )}

          {extraLabel && (
            <Box
              component="span"
              sx={{
                ml: "auto",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {extraLabel}
            </Box>
          )}
        </FormLabel>
      )}

      {/* Input or Custom Children */}
      {children ? (
        typeof children === "function" ? (
          children({ id, error: isError, disabled, readOnly, fullWidth, size })
        ) : (
          children
        )
      ) : (
        <CommonInput
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
          size={size}
          fullWidth={fullWidth}
          width={width}
          startAdornment={startAdornment}
          endAdornment={endAdornment}
          disabled={disabled}
          readOnly={readOnly}
          error={isError}
          inputProps={inputProps}
          sx={inputSx}
          {...restProps}
        />
      )}

      {/* Helper / Error Text */}
      {!hideHelperText && resolvedHelperText && (
        <FormHelperText
          error={isError}
          sx={{
            fontSize: "12px",
            mt: 0.75,
            mx: 0.5,
            color: isError
              ? resolvedRequiredColor
              : colors.slate[500],
            ...helperTextSx,
          }}
        >
          {resolvedHelperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

CommonFormField.propTypes = {
  // Identification & Layout
  id: PropTypes.string,
  name: PropTypes.string,
  fullWidth: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),

  // Label & Header
  label: PropTypes.node,
  icon: PropTypes.node,
  iconColor: PropTypes.string,
  iconSize: PropTypes.number,
  required: PropTypes.bool,
  requiredColor: PropTypes.string,
  extraLabel: PropTypes.node,
  hideLabel: PropTypes.bool,
  labelSx: PropTypes.object,

  // Helper & Error Text
  helperText: PropTypes.node,
  errorText: PropTypes.node,
  hideHelperText: PropTypes.bool,
  helperTextSx: PropTypes.object,

  // CommonInput props
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  inputProps: PropTypes.object,
  inputSx: PropTypes.object,

  // Children & Styling
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  sx: PropTypes.object,
  formControlProps: PropTypes.object,
};

export default CommonFormField;
